import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../layout/Layout.js';
import Section from '../../common/Section.js';
import Button from '../../common/Button.js';
import styled from 'styled-components';

const BlogContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  
  img {
    width: 100%;
    height: auto;
    border-radius: 8px;
    margin-bottom: 2rem;
  }
  
  .meta {
    display: flex;
    gap: 1rem;
    font-size: 0.875rem;
    color: var(--gray);
    margin-bottom: 1rem;
    
    .category {
      color: var(--secondary);
      font-weight: 600;
    }
  }
  
  h1 {
    margin-bottom: 1.5rem;
  }
  
  h2 {
    font-size: 1.75rem;
    margin: 2rem 0 1rem;
    color: var(--primary);
  }
  
  p {
    margin-bottom: 1.5rem;
    line-height: 1.7;
  }
  
  ul {
    margin: 0 0 1.5rem 2rem;
    
    li {
      margin-bottom: 0.5rem;
    }
  }
  
  .intro {
    font-size: 1.2rem;
    line-height: 1.8;
    margin-bottom: 2rem;
    padding: 1.5rem;
    background-color: rgba(52, 152, 219, 0.05);
    border-radius: 8px;
    color: var(--dark);
  }
  
  .highlight {
    background-color: rgba(52, 152, 219, 0.1);
    border-left: 4px solid var(--secondary);
    padding: 1rem;
    margin-bottom: 1.5rem;
  }
  
  .final-thought {
    background-color: var(--light-gray);
    padding: 1.5rem;
    border-radius: 8px;
    margin: 2rem 0;
  }
  
  .back-button {
    margin-top: 3rem;
    display: flex;
    justify-content: space-between;
    
    @media (max-width: 768px) {
      flex-direction: column;
      gap: 1rem;
    }
  }
`;

const LandDevelopmentPage = () => {
  return (
    <Layout>
      <Section>
        <div className="container">
          <BlogContent>
            <img src="/images/blog3.png" alt="Land Development Partnership in NC Triad" />
            
            <div className="meta">
              <span className="date">April 17, 2023</span>
              <span className="category">Land Development</span>
            </div>
            
            <h1>Partnering for Land Development: A Smart Investment Opportunity</h1>
            
            <div className="intro">
              <p>The North Carolina Triad region — encompassing Greensboro, Winston-Salem, and High Point — offers compelling real estate investment opportunities. With its strong economy, population growth, and quality of life, the Triad continues to attract smart investors and developers.</p>
              
              <p>One of the Triad's most appealing features is its affordability compared to larger metro areas, making it an excellent entry point for land development. With increasing demand for well-planned residential communities, this is the ideal time to partner with experienced local builders and unlock the full potential of buildable land.</p>
              
              <p>Whether you're a landowner looking to develop, or an investor seeking long-term value — we invite you to explore a strategic partnership with Shoffner Homes.</p>
            </div>
            
            <h2>1. We're Looking for Strategic Land Opportunities</h2>
            <p>We're currently seeking landowners who want to unlock the full value of their property. If you own land in a growing part of Guilford County or nearby, we want to talk. The ideal site:</p>
            <ul>
              <li>Has road access or a clear path to create it</li>
              <li>Can be subdivided into multiple home sites</li>
              <li>Is priced appropriately for residential development</li>
              <li>Fits zoning regulations or can be rezoned with minimal resistance</li>
            </ul>
            
            <h2>2. Why Partner With Shoffner Homes?</h2>
            <p>With over 40 years of experience building quality homes across North Carolina, Shoffner Homes understands what it takes to bring residential communities to life — efficiently and profitably.</p>
            
            <p>When you partner with us, you benefit from:</p>
            <ul>
              <li>A local builder who understands permitting, utility access, and municipal requirements</li>
              <li>Transparent budgeting and joint decision-making</li>
              <li>A focus on building homes that hold value — not just flipping lots</li>
            </ul>
            
            <h2>3. Equal Investment, Equal Reward</h2>
            <p>We're looking for true partnerships. You bring the land. We bring the development experience.</p>
            <ul>
              <li>We invest in site plans, permitting, and infrastructure</li>
              <li>We build the homes</li>
              <li>Together, we share in the profit when lots or homes are sold</li>
            </ul>
            
            <div className="highlight">
              <p>This is not a land grab. It's a strategic joint venture that makes financial sense for both parties in a region showing strong economic indicators and housing demand.</p>
            </div>
            
            <h2>4. Feasibility Comes First</h2>
            <p>Not every parcel of land is a good fit — and that's okay. We start with smart due diligence:</p>
            <ul>
              <li>Evaluate topography, access, and utilities</li>
              <li>Review zoning codes, setbacks, and lot layout potential</li>
              <li>Get realistic development and construction cost estimates</li>
            </ul>
            
            <h2>5. What We're NOT Looking For</h2>
            <p>To protect both parties, we steer clear of:</p>
            <ul>
              <li>Land priced above market for the area</li>
              <li>Parcels that can't be subdivided, perked, or accessed</li>
              <li>Deals where either side controls everything</li>
            </ul>
            
            <h2>6. Ready to Talk?</h2>
            <p>If you own land — or know someone who does — let's explore the possibilities.</p>
            <p>We'll review your site for free, with no pressure. Just a real conversation.</p>
            
            <div className="final-thought">
              <p>At Shoffner Homes, we believe in creating win-win partnerships that build lasting value. If your land has potential, we'd love to discuss how we can work together to develop homes and strengthen our local community.</p>
            </div>
            
            <div className="back-button">
              <Button as={Link} to="/blog" variant="outline">
                Back to Blog
              </Button>
              <Button as={Link} to="/contact" variant="primary">
                Contact Us About Your Land
              </Button>
            </div>
            
          </BlogContent>
        </div>
      </Section>
    </Layout>
  );
};

export default LandDevelopmentPage;